import { useState, type ReactNode } from "react"
import { ChevronDown } from "lucide-react"

interface FormSectionProps {
  title: string
  defaultExpanded?: boolean
  errorCount?: number
  children: ReactNode
  className?: string
}

/**
 * Collapsible accordion section for organizing form fields.
 * Shows error count badge when collapsed and has validation errors.
 */
export function FormSection({
  title,
  defaultExpanded = true,
  errorCount = 0,
  children,
  className = "",
}: FormSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div className={`border border-slate-200/50 rounded-lg overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50/50 hover:bg-slate-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800">{title}</span>
          {!isExpanded && errorCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-red-500 rounded-full">
              {errorCount}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {isExpanded && <div className="px-4 py-4 space-y-4">{children}</div>}
    </div>
  )
}
